/**
 * WIP (Work In Progress) Limit Utilities
 * 
 * Functions for checking and enforcing WIP limits on statuses.
 * WIP limits help prevent overloading columns in Kanban view.
 */

/**
 * Check if a task can be moved to a status based on WIP limits
 * 
 * @param {string} statusId - The target status ID
 * @param {Array} tasks - All tasks in the sprint
 * @param {Array} statuses - All statuses in the sprint
 * @param {string} [excludeTaskId] - Task ID to exclude from count (the task being moved)
 * @returns {Object} { allowed: boolean, reason?: string, currentCount: number, limit: number|null }
 */
export const canMoveToStatus = (statusId, tasks, statuses, excludeTaskId = null) => {
  const status = statuses?.find(s => s.id === statusId);
  
  // No status found - allow (shouldn't happen, but be safe)
  if (!status) {
    return { allowed: true, currentCount: 0, limit: null };
  }
  
  // Blocked category statuses are never limited
  // (being blocked is beyond user control)
  if (status.category === 'blocked') {
    return { allowed: true, currentCount: 0, limit: null, isBlocked: true };
  }
  
  // No limit set - allow
  if (!status.wipLimit && status.wipLimit !== 0) {
    const currentCount = tasks?.filter(t => t.status === statusId).length || 0;
    return { allowed: true, currentCount, limit: null };
  }
  
  // Count tasks currently in this status (excluding the task being moved)
  const currentCount = tasks?.filter(t => 
    t.status === statusId && t.id !== excludeTaskId
  ).length || 0;
  
  // Check against limit
  if (currentCount >= status.wipLimit) {
    return {
      allowed: false,
      reason: `"${status.name}" is at capacity (${currentCount}/${status.wipLimit})`,
      currentCount,
      limit: status.wipLimit,
    };
  }
  
  return { 
    allowed: true, 
    currentCount, 
    limit: status.wipLimit,
  };
};

/**
 * Get the WIP status for a column (for display purposes)
 * 
 * @param {string} statusId - The status ID
 * @param {Array} tasks - All tasks in the sprint
 * @param {Array} statuses - All statuses in the sprint
 * @returns {Object} { count, limit, isAtLimit, isOverLimit, hasLimit }
 */
export const getWipStatus = (statusId, tasks, statuses) => {
  const status = statuses?.find(s => s.id === statusId);
  const count = tasks?.filter(t => t.status === statusId).length || 0;
  
  // Blocked category - never show limits
  if (status?.category === 'blocked') {
    return { count, limit: null, isAtLimit: false, isOverLimit: false, hasLimit: false };
  }
  
  const limit = status?.wipLimit;
  const hasLimit = limit !== null && limit !== undefined;
  
  return {
    count,
    limit: hasLimit ? limit : null,
    isAtLimit: hasLimit && count === limit,
    isOverLimit: hasLimit && count > limit,
    hasLimit,
  };
};

/**
 * Check if any status would be violated by moving a task
 * Used for showing warnings before drag operations
 * 
 * @param {string} taskId - The task being moved
 * @param {string} targetStatusId - The target status
 * @param {Array} tasks - All tasks
 * @param {Array} statuses - All statuses
 * @returns {Object} Result from canMoveToStatus
 */
export const checkWipBeforeMove = (taskId, targetStatusId, tasks, statuses) => {
  return canMoveToStatus(targetStatusId, tasks, statuses, taskId);
};

/**
 * Update a status's WIP limit
 * Returns a new statuses array with the updated limit
 * 
 * @param {Array} statuses - Current statuses array
 * @param {string} statusId - Status to update
 * @param {number|null} newLimit - New limit (null to remove limit)
 * @returns {Array} New statuses array
 */
export const updateStatusWipLimit = (statuses, statusId, newLimit) => {
  return statuses.map(status => {
    if (status.id === statusId) {
      return { ...status, wipLimit: newLimit };
    }
    return status;
  });
};

export default {
  canMoveToStatus,
  getWipStatus,
  checkWipBeforeMove,
  updateStatusWipLimit,
};

